import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { forwardRef } from "react";
import { type Control, Controller } from "react-hook-form";

import { type ReferenceFormData } from "../types";
import { getReferenceFormFieldErrorId } from "../utils/formHelpers";

interface MonthYearPickerFieldProps {
  id: string;
  name: keyof ReferenceFormData;
  control: Control<ReferenceFormData>;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  title?: string;
  enterKeyHint?: string;
  onAccept?: () => void;
}

export const MonthYearPickerField = forwardRef<HTMLInputElement, MonthYearPickerFieldProps>(
  (props, inputRef) => {
    const {
      id,
      name,
      control,
      placeholder,
      minDate,
      maxDate,
      title,
      enterKeyHint = "next",
      onAccept,
    } = props;

    return (
      <Controller
        name={name}
        control={control}
        render={({
          field: { ref, onBlur, ...field },
          fieldState: { error },
          formState: { isSubmitting },
        }) => {
          const errorId = getReferenceFormFieldErrorId(id);
          const value = field.value ?? null;

          return (
            <Stack>
              <MobileDatePicker
                {...field}
                disableFuture
                inputRef={(element) => {
                  // Set both react-hook-form's ref and the forwarded ref
                  ref(element);
                  if (typeof inputRef === "function") {
                    inputRef(element);
                  } else if (inputRef) {
                    inputRef.current = element;
                  }
                }}
                value={value}
                views={["month", "year"]}
                openTo="month"
                format="MM/yyyy"
                minDate={minDate}
                maxDate={maxDate}
                disabled={isSubmitting}
                slotProps={{
                  textField: {
                    id,
                    placeholder: placeholder ?? "MM/YYYY",
                    error: Boolean(error),
                    inputProps: {
                      "aria-describedby": isDefined(error) ? errorId : undefined,
                      "aria-label": title,
                      enterKeyHint,
                    },
                    // Align styles with large input fields. There is a size prop for `textField` but there are only 'small' and 'medium' options.
                    sx: (theme) => ({
                      "& .MuiOutlinedInput-root": {
                        height: theme.size?.input?.large,
                        borderRadius: theme.borderRadius?.medium,
                      },
                      "& .MuiOutlinedInput-input": {
                        paddingLeft: theme.spacing(7),
                        paddingRight: theme.spacing(2),
                        fontSize: theme.typography.body1.fontSize,
                      },
                    }),
                  },
                }}
                localeText={{
                  toolbarTitle: title,
                }}
                onClose={onBlur}
                onAccept={onAccept}
              />
              {isDefined(error) && (
                <Text
                  id={errorId}
                  variant="caption"
                  color="error"
                  sx={{ paddingLeft: 1, marginTop: 1 }}
                  role="alert"
                >
                  {error.message}
                </Text>
              )}
            </Stack>
          );
        }}
      />
    );
  }
);

MonthYearPickerField.displayName = "MonthYearPickerField";
